import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Navigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import {
  Radio,
  Bot,
  MessageSquareDashed,
  Send,
  Users,
  Brain,
  Rocket,
  Shield,
  CreditCard,
  BookOpen,
  CheckCircle,
  Zap,
  Globe,
  Coins,
  MessageSquare,
  MessagesSquare,
  Sparkles,
  TrendingUp,
  BarChart3,
  Activity,
  Database,
  Workflow,
} from "lucide-react";
import PageSeo from "@/components/PageSeo";

interface CountryRow {
  country_code: string;
  country_name: string;
  points_per_egp: number;
  min_purchase_points: number;
}
interface TierRow {
  tier_order: number;
  min_points: number;
  max_points: number | null;
  discount_percent: number;
}

const Index = () => {
  const { user, loading } = useAuth();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const [country, setCountry] = useState<CountryRow | null>(null);
  const [tiers, setTiers] = useState<TierRow[]>([]);
  const [bonus, setBonus] = useState<{ total_points: number; breakdown: { campaign_messages: number; comment_replies: number; messenger_replies: number; ai_tokens: number } } | null>(null);
  const [homePath, setHomePath] = useState<string | null>(null);
  const [homeLoaded, setHomeLoaded] = useState(false);

  useEffect(() => {
    if (!user) { setHomeLoaded(true); return; }
    Promise.all([
      (supabase as any).from("user_settings").select("home_path").eq("user_id", user.id).maybeSingle(),
      (supabase as any).from("user_roles").select("role").eq("user_id", user.id),
    ]).then(([settingsRes, rolesRes]: any) => {
      const isAdmin = rolesRes.data?.some((r: any) => r.role === "admin" || r.role === "superadmin");
      const defaultPath = isAdmin ? "/admin-dashboard" : "/dashboard";
      setHomePath(settingsRes.data?.home_path || defaultPath);
      setHomeLoaded(true);
    });
  }, [user]);

  useEffect(() => {
    if (user) return;
    (async () => {
      try {
        const { data } = await supabase.functions.invoke("public-pricing-packages");
        if (data?.country) setCountry(data.country);
        if (data?.tiers) setTiers(data.tiers);
      } catch (_) {}
      try {
        const { data } = await supabase.functions.invoke("public-welcome-bonus");
        if (data?.total_points) setBonus(data);
      } catch (_) {}
    })();
  }, [user]);

  if (loading || (user && !homeLoaded))
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  if (user) return <Navigate to={homePath || "/dashboard"} replace />;

  const moduleSections = [
    {
      titleKey: "home.moduleSection1Title",
      items: [
        { icon: Radio, titleKey: "home.featureCampaigns", descKey: "home.featureCampaignsDesc" },
        { icon: Bot, titleKey: "home.featureMessengerBot", descKey: "home.featureMessengerBotDesc" },
        { icon: MessageSquareDashed, titleKey: "home.featureCommentsBot", descKey: "home.featureCommentsBotDesc" },
        { icon: Workflow, titleKey: "home.featureScenarios", descKey: "home.featureScenariosDesc" },
      ],
    },
    {
      titleKey: "home.moduleSection2Title",
      items: [
        { icon: BarChart3, titleKey: "home.featurePageAnalysis", descKey: "home.featurePageAnalysisDesc" },
        { icon: Activity, titleKey: "home.featureBrandListening", descKey: "home.featureBrandListeningDesc" },
        { icon: Users, titleKey: "home.featureAudience", descKey: "home.featureAudienceDesc" },
        { icon: Shield, titleKey: "home.featureAnalytics", descKey: "home.featureAnalyticsDesc" },
      ],
    },
    {
      titleKey: "home.moduleSection3Title",
      items: [
        { icon: Brain, titleKey: "home.featureAI", descKey: "home.featureAIDesc" },
        { icon: Database, titleKey: "home.featureKnowledge", descKey: "home.featureKnowledgeDesc" },
      ],
    },
  ];

  return (
    <div dir={isRTL ? "rtl" : "ltr"} className="min-h-screen bg-background">
      <PageSeo
        title="CommentsBot — Facebook campaigns, chatbots & comment automation"
        description="CommentsBot helps Facebook page owners send Messenger campaigns, automate replies to comments, and run AI chatbots — all from one dashboard."
        path="/"
      />
      {/* Nav */}
      <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2 shrink-0">
            <div className="gradient-primary p-2 rounded-lg">
              <Radio className="h-5 w-5 text-primary-foreground" />
            </div>
            <span className="font-heading font-bold text-lg hidden min-[420px]:inline">CommentsBot</span>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-3">
            <Link to="/guide" target="_blank" aria-label={t("nav.guide")}>
              <Button variant="ghost" size="sm" className="gap-1.5 px-2 sm:px-3">
                <BookOpen className="h-4 w-4" />
                <span className="hidden sm:inline">{t("nav.guide")}</span>
              </Button>
            </Link>
            <LanguageSwitcher compact className="px-2 sm:px-3" />
            <Link to="/auth">
              <Button size="sm" className="px-3">
                {t("home.login")}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main>
        {/* Old CommentsBot legacy app link - top */}
        <section className="container mx-auto px-4 pt-6">
          <div className="max-w-3xl mx-auto rounded-xl border bg-card p-4 sm:p-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-center sm:text-start">
            <h3 className="text-base sm:text-lg font-heading font-semibold">
              CommentsBot old version / بوت التعليقات النسخه السابقه
            </h3>
            <a href="https://www.bussinessmanager.com/login/login" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="gap-2">
                <Globe className="h-4 w-4" />
                {isRTL ? "فتح" : "Open"}
              </Button>
            </a>
          </div>
        </section>

        {/* Hero */}
        <section className="container mx-auto px-4 py-16 sm:py-24 text-center">
          <Badge variant="secondary" className="mb-4 gap-1.5 px-3 py-1">
            <Zap className="h-3.5 w-3.5" />
            {t("home.freeTrial", { points: bonus?.total_points ?? 306 })}
          </Badge>
          <h1 className="text-3xl sm:text-5xl font-heading font-bold max-w-3xl mx-auto leading-tight">
            {t("home.heroTitle")}
          </h1>
          <p className="mt-4 text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto">{t("home.heroSubtitle")}</p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/auth">
              <Button size="lg" className="gap-2 px-8">
                <Rocket className="h-5 w-5" />
                {t("home.getStarted")}
              </Button>
            </Link>
            <Link to="/guide" target="_blank" aria-label={`${t("home.learnMore")} — ${t("nav.guide")}`}>
              <Button variant="outline" size="lg" className="gap-2">
                <BookOpen className="h-5 w-5" />
                {t("home.learnMore")}
              </Button>
            </Link>
          </div>
        </section>

        {/* Welcome Bonus - Highlighted Banner for new users */}
        <section className="container mx-auto px-4 pb-8">
          <div className="max-w-4xl mx-auto relative overflow-hidden rounded-2xl border-2 border-primary/30 shadow-2xl bg-gradient-to-br from-primary/10 via-primary/5 to-background">
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-primary/10 rounded-full blur-2xl translate-y-1/2 -translate-x-1/2" />

            <div className="relative p-8 sm:p-10 text-center space-y-5">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-full shadow-lg">
                <Sparkles className="h-4 w-4" />
                <span className="text-sm font-semibold">{t("home.freeTrial", { points: bonus?.total_points ?? 306 })}</span>
              </div>

              {/* Title */}
              <div className="flex items-center justify-center gap-3">
                <Coins className="h-8 w-8 text-primary" />
                <span className="text-2xl sm:text-3xl font-heading font-bold">{t("home.pricingPlanName")}</span>
              </div>

              {/* Big Points Display */}
              <div className="bg-background/80 backdrop-blur rounded-2xl p-6 max-w-sm mx-auto border border-primary/20 shadow-inner">
                <div className="flex items-baseline justify-center gap-2">
                  <span className="text-6xl sm:text-7xl font-bold text-primary">{bonus?.total_points ?? 306}</span>
                  <span className="text-xl text-muted-foreground">{t("home.pricingFreePoints")}</span>
                </div>
                <p className="text-sm text-success font-semibold mt-2 bg-success/10 inline-block px-3 py-1 rounded-full">
                  {t("home.pricingValueNote")}
                </p>
              </div>

              {/* Benefits */}
              <ul className="text-sm text-start space-y-2.5 mx-auto max-w-sm">
                {[
                  { k: "pricingBenefit1", count: bonus?.breakdown.campaign_messages },
                  { k: "pricingBenefit2", count: bonus?.breakdown.comment_replies },
                  { k: "pricingBenefit3", count: bonus?.breakdown.messenger_replies },
                  { k: "pricingBenefit4" },
                  { k: "pricingBenefit5" },
                ].map(({ k, count }) => (
                  <li key={k} className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                    <span>{t(`home.${k}`, count != null ? { count: count.toLocaleString() } : {})}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <Link to="/auth">
                <Button className="w-full max-w-sm mt-2 gap-2 shadow-lg" size="lg">
                  <Rocket className="h-4 w-4" />
                  {t("home.startFreeTrial")}
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* What you get with the welcome bonus */}
        {bonus && (
          <section className="container mx-auto px-4 py-12">
            <div className="text-center mb-8">
              <h2 className="text-2xl sm:text-3xl font-heading font-bold">
                {t("home.whatYouGetTitle", { points: bonus.total_points })}
              </h2>
              <p className="text-muted-foreground mt-2">{t("home.whatYouGetSubtitle")}</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-5xl mx-auto">
              {[
                { icon: Send, value: bonus.breakdown.campaign_messages, key: "campaignMessages" },
                { icon: MessageSquareDashed, value: bonus.breakdown.comment_replies, key: "commentReplies" },
                { icon: Bot, value: bonus.breakdown.messenger_replies, key: "messengerReplies" },
                { icon: Sparkles, value: bonus.breakdown.ai_tokens, key: "aiTokens" },
              ].map((item) => (
                <Card key={item.key} className="border-primary/20 hover:border-primary/40 transition-colors">
                  <CardContent className="p-5 text-center space-y-2">
                    <div className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-primary/10">
                      <item.icon className="h-5 w-5 text-primary" />
                    </div>
                    <div className="text-2xl sm:text-3xl font-bold text-primary">
                      {item.value.toLocaleString()}
                    </div>
                    <div className="text-sm text-muted-foreground font-medium">
                      {t(`home.whatYouGet.${item.key}`)}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <p className="text-center text-xs text-muted-foreground mt-6">
              {t("home.whatYouGetFootnote")}
            </p>
          </section>
        )}

        <section className="container mx-auto px-4 py-16">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-heading font-bold">{t("home.modulesTitle")}</h2>
            <p className="text-muted-foreground mt-2">{t("home.modulesSubtitle")}</p>
          </div>
          <div className="space-y-12 max-w-5xl mx-auto">
            {moduleSections.map((section, sIdx) => (
              <div key={sIdx}>
                <h3 className="text-lg font-heading font-semibold mb-4 flex items-center gap-2">
                  <span className="h-1.5 w-8 rounded-full bg-primary" />
                  {t(section.titleKey)}
                </h3>
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {section.items.map((f, i) => (
                    <Card key={i} className="group hover:shadow-md hover:border-primary/30 transition-all">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="p-2 rounded-lg bg-primary/10">
                            <f.icon className="h-5 w-5 text-primary" />
                          </div>
                          <h4 className="font-semibold">{t(f.titleKey)}</h4>
                        </div>
                        <p className="text-sm text-muted-foreground">{t(f.descKey)}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-muted-foreground mt-10">{t("home.trustedBy")}</p>
        </section>

        {/* Per-service rates */}
        <section className="bg-muted/30 py-16">
          <div className="container mx-auto px-4">
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl font-heading font-bold">{t("home.ratesTitle")}</h2>
              <p className="text-muted-foreground mt-2">{t("home.ratesSubtitle")}</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 max-w-5xl mx-auto">
              {[
                { icon: Send, titleKey: "home.rateCampaign", valueKey: "home.rateCampaignValue" },
                { icon: MessageSquare, titleKey: "home.rateComments", valueKey: "home.rateCommentsValue" },
                { icon: MessagesSquare, titleKey: "home.rateMessenger", valueKey: "home.rateMessengerValue" },
                { icon: Sparkles, titleKey: "home.rateAI", valueKey: "home.rateAIValue" },
              ].map((r, i) => (
                <Card key={i} className="text-center">
                  <CardContent className="p-5 space-y-2">
                    <div className="inline-flex p-2 rounded-lg bg-primary/10">
                      <r.icon className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="font-semibold text-sm">{t(r.titleKey)}</h3>
                    <p className="text-sm text-primary font-medium">{t(r.valueKey)}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing + Tiers */}
        <section className="bg-muted/50 py-16">
          <div className="container mx-auto px-4">
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl font-heading font-bold">{t("home.pricingTitle")}</h2>
              <p className="text-muted-foreground mt-2">{t("home.pricingSubtitle")}</p>
            </div>

            {/* Volume tiers — country-aware packages */}
            <div className="max-w-4xl mx-auto mb-10">
              <div className="text-center mb-6">
                <h3 className="text-lg font-heading font-semibold flex items-center justify-center gap-2">
                  <TrendingUp className="h-5 w-5 text-primary" />
                  {t("home.tiersTitle")}
                </h3>
                <p className="text-sm text-muted-foreground mt-1">{t("home.tiersSubtitle")}</p>
                {country && (
                  <Badge variant="secondary" className="mt-3 gap-1">
                    <Globe className="h-3 w-3" />
                    {t("home.yourCountryRate", { country: country.country_name, rate: country.points_per_egp })}
                  </Badge>
                )}
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                {(() => {
                  const list = tiers.length
                    ? tiers
                    : [
                        { tier_order: 1, min_points: 100, max_points: 999, discount_percent: 0 },
                        { tier_order: 2, min_points: 1000, max_points: 9999, discount_percent: 15 },
                        { tier_order: 3, min_points: 10000, max_points: null, discount_percent: 30 },
                      ];
                  const rate = country?.points_per_egp ?? 1;
                  const popularPts = 3000;
                  const popularIdx = list.findIndex(
                    (t) => popularPts >= t.min_points && (t.max_points == null || popularPts <= t.max_points),
                  );
                  return list.map((tier, i) => {
                    const highlight = i === popularIdx;
                    const pts = highlight ? popularPts : tier.min_points;
                    const egp = (pts / rate) * (1 - tier.discount_percent / 100);
                    return (
                      <Card
                        key={tier.tier_order}
                        className={highlight ? "border-primary/40 shadow-md relative" : "relative"}
                      >
                        {highlight && (
                          <Badge className="absolute -top-2 left-1/2 -translate-x-1/2">
                            {t("home.packageMostPopular")}
                          </Badge>
                        )}
                        <CardContent className="p-5 text-center space-y-2">
                          <p className="text-2xl font-heading font-bold">
                            {pts.toLocaleString()}{" "}
                            <span className="text-sm font-normal text-muted-foreground">{t("home.packagePoints")}</span>
                          </p>
                          <p className={`text-xl font-bold ${highlight ? "text-primary" : ""}`}>
                            {egp.toFixed(2)} <span className="text-sm font-normal text-muted-foreground">EGP</span>
                          </p>
                          <p className="text-xs">
                            {tier.discount_percent > 0 ? (
                              <span className="text-success font-semibold">
                                {t("home.packageSave", { percent: tier.discount_percent })}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">{t("home.packageStandard")}</span>
                            )}
                          </p>
                          <Link to="/auth">
                            <Button size="sm" variant={highlight ? "default" : "outline"} className="w-full mt-2">
                              {t("home.packageBuyNow")}
                            </Button>
                          </Link>
                        </CardContent>
                      </Card>
                    );
                  });
                })()}
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="container mx-auto px-4 py-16">
          <div className="max-w-3xl mx-auto text-center rounded-2xl border bg-gradient-to-br from-primary/10 via-background to-background p-8 sm:p-12">
            <h2 className="text-2xl sm:text-3xl font-heading font-bold">{t("home.ctaTitle")}</h2>
            <p className="text-muted-foreground mt-2">{t("home.ctaSubtitle")}</p>
            <Link to="/auth" className="inline-block mt-6">
              <Button size="lg" className="gap-2 px-8">
                <Rocket className="h-5 w-5" />
                {t("home.ctaButton")}
              </Button>
            </Link>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t py-8">
        <div className="container mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            <span>CommentsBot &copy; 2017</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/guide" target="_blank" className="hover:text-foreground transition-colors">
              {t("nav.guide")}
            </Link>
            <Link to="/pricing" className="hover:text-foreground transition-colors">
              {t("nav.pricing")}
            </Link>
            <Link to="/contact" className="hover:text-foreground transition-colors">
              {t("nav.contact")}
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
